import React, { useState } from "react";
import { FileText, ScrollText, Shield } from "lucide-react";

export default function Legal() {
  const [tab, setTab] = useState("mentions");

  return (
    <div className="min-h-screen bg-black text-white py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-12">
          <p className="text-gold text-xs tracking-[4px] uppercase mb-3">Informations légales</p>
          <h1 className="text-4xl md:text-5xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}>
            Mentions Légales & CGV
          </h1>
          <div className="w-16 h-px bg-gold mx-auto" />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {[
            { id: "mentions", label: "Mentions Légales", Icon: FileText },
            { id: "cgv", label: "CGV", Icon: ScrollText },
            { id: "privacy", label: "Confidentialité (RGPD)", Icon: Shield },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`legal-tab-${t.id}`}
              className={`px-5 py-2.5 text-xs uppercase tracking-wider border flex items-center gap-2 transition-colors ${
                tab === t.id ? "border-gold bg-gold text-black" : "border-white/20 text-white/70 hover:border-gold"
              }`}
            >
              <t.Icon size={14} />
              {t.label}
            </button>
          ))}
        </div>

        <div className="prose prose-invert max-w-none text-gray-300 leading-relaxed">
          {tab === "mentions" && <MentionsLegales />}
          {tab === "cgv" && <CGV />}
          {tab === "privacy" && <Privacy />}
        </div>

        <p className="text-center text-gray-500 text-xs mt-16">Dernière mise à jour : février 2026</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-2xl text-gold mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{title}</h2>
      <div className="text-sm space-y-3">{children}</div>
    </section>
  );
}

function MentionsLegales() {
  return (
    <>
      <Section title="Éditeur du site">
        <p><strong className="text-white">Garcia Ludovic — Photographe</strong></p>
        <p>Adresse : 270 rue Capitaine, 13270 Fos-sur-Mer, France</p>
        <p>Email : contact@ludovic-garcia-photographe.fr</p>
        <p>Téléphone : 06 26 63 12 79</p>
        <p className="text-gray-500 italic">SIRET : [à compléter] — TVA intracommunautaire : [à compléter si applicable]</p>
        <p className="text-gray-500 italic">Statut : Auto-entrepreneur / Micro-entreprise [à confirmer]</p>
      </Section>

      <Section title="Directeur de la publication">
        <p>Monsieur Ludovic Garcia</p>
      </Section>

      <Section title="Hébergement">
        <p>Le site est hébergé par <strong className="text-white">Emergent Platform</strong> (infrastructure cloud basée dans l'Union européenne).</p>
        <p>Site : <a href="https://emergent.sh" className="text-gold hover:underline">emergent.sh</a></p>
      </Section>

      <Section title="Propriété intellectuelle">
        <p>L'ensemble du contenu du site <strong className="text-white">ludovic-garcia-photographe.fr</strong> (photographies, textes, logos, charte graphique, code source) est la propriété exclusive de Ludovic Garcia et est protégé par les lois françaises et internationales relatives au <strong className="text-white">droit d'auteur</strong> et à la <strong className="text-white">propriété intellectuelle</strong>.</p>
        <p>Toute reproduction, représentation, modification, publication, adaptation, téléchargement ou exploitation, totale ou partielle, des éléments du site, quel que soit le moyen ou le procédé utilisé, est <strong className="text-red-400">strictement interdite sans autorisation écrite préalable</strong> de Ludovic Garcia.</p>
        <p>Toute utilisation non autorisée constitue une <strong>contrefaçon</strong> sanctionnée par les articles L.335-2 et suivants du Code de la propriété intellectuelle.</p>
      </Section>

      <Section title="Photographies — droit à l'image">
        <p>Les personnes visibles sur les photographies publiées ont donné leur accord écrit pour leur diffusion. Toute personne se reconnaissant et souhaitant que son image soit retirée peut en faire la demande à <a href="mailto:contact@ludovic-garcia-photographe.fr" className="text-gold hover:underline">contact@ludovic-garcia-photographe.fr</a>.</p>
      </Section>

      <Section title="Limitation de responsabilité">
        <p>Ludovic Garcia s'efforce de maintenir le site accessible et ses informations à jour, mais ne peut garantir l'absence d'erreurs ou d'interruptions. Sa responsabilité ne saurait être engagée en cas de dommages directs ou indirects liés à l'utilisation du site.</p>
      </Section>

      <Section title="Droit applicable">
        <p>Le présent site est soumis au droit français. Tout litige relève de la compétence exclusive des tribunaux français.</p>
      </Section>
    </>
  );
}

function CGV() {
  return (
    <>
      <Section title="1. Objet">
        <p>Les présentes Conditions Générales de Vente (CGV) régissent les prestations de services photographiques proposées par <strong className="text-white">Garcia Ludovic — Photographe</strong> (ci-après "le Photographe") à toute personne physique ou morale (ci-après "le Client") souhaitant bénéficier de ces services via le site <strong>ludovic-garcia-photographe.fr</strong>.</p>
      </Section>

      <Section title="2. Prestations">
        <p>Le Photographe propose différentes prestations (shooting, séance grossesse, naissance, baptême, mariage, etc.) dont les tarifs et contenus sont précisés sur la page <a href="/tarifs" className="text-gold hover:underline">Tarifs</a>.</p>
        <p>Les cartes cadeaux sont également disponibles sur la page <a href="/cartes-cadeaux" className="text-gold hover:underline">Cartes Cadeaux</a>.</p>
      </Section>

      <Section title="3. Réservation et acompte">
        <p><strong className="text-white">Toute réservation est confirmée uniquement après versement d'un acompte de 30 %</strong> du montant total de la prestation.</p>
        <p>Tant que l'acompte n'est pas reçu, le créneau reste disponible à la réservation par d'autres clients.</p>
        <p>Modes de paiement acceptés pour l'acompte : <strong className="text-white">virement bancaire</strong> ou <strong className="text-white">Wero</strong> (coordonnées transmises par email sécurisé après validation).</p>
        <p>Le solde est à régler le jour de la séance, en espèces ou par virement.</p>
      </Section>

      <Section title="4. Frais de déplacement">
        <p>Les séances sont basées à <strong>Fos-sur-Mer</strong>. Les frais de déplacement au-delà de 20 km sont facturés :</p>
        <ul className="list-disc ml-6 space-y-1">
          <li><strong className="text-white">0,50 €/km</strong> aller-retour (route sans péage)</li>
          <li><strong className="text-white">0,60 €/km</strong> aller-retour (route avec péage — forfait incluant l'estimation des péages)</li>
        </ul>
        <p>Le montant exact est calculé automatiquement lors de la réservation en fonction de la distance routière (service Google Maps).</p>
      </Section>

      <Section title="5. Annulation et remboursement">
        <p className="text-red-400"><strong>⚠️ L'acompte de 30 % est non-remboursable en cas d'annulation par le Client</strong>, quel que soit le motif (hors cas de force majeure dûment justifié).</p>
        <p>En cas d'annulation par le Photographe (maladie, accident, cas de force majeure), l'acompte est intégralement remboursé ou la séance reportée d'un commun accord.</p>
        <p><strong className="text-white">Report de la séance :</strong> le Client peut demander un report au plus tard 48 heures avant la date prévue, sous réserve de disponibilité. Au-delà, l'acompte reste acquis au Photographe.</p>
      </Section>

      <Section title="6. Droit de rétractation">
        <p>Conformément à l'article L.221-28 du Code de la consommation, le droit de rétractation ne s'applique pas aux prestations de services entièrement exécutées avant la fin du délai de rétractation, ni aux services fournis à une date ou période déterminée (ce qui est le cas des séances photographiques réservées à date fixe).</p>
        <p>Pour les cartes cadeaux non utilisées et achetées à distance, le Client dispose d'un délai de <strong className="text-white">14 jours</strong> pour se rétracter après l'achat, à condition que la carte n'ait pas encore été utilisée.</p>
      </Section>

      <Section title="7. Droits sur les photographies">
        <p><strong className="text-white">Cession de droits :</strong> le Client obtient un droit d'usage personnel et familial des photographies (impression, partage privé). Toute utilisation commerciale ou publicitaire nécessite un accord écrit séparé avec le Photographe.</p>
        <p><strong className="text-white">Droit de diffusion :</strong> sauf mention contraire expresse, le Photographe se réserve le droit d'utiliser les photographies pour son portfolio et ses réseaux sociaux professionnels. Le Client peut s'y opposer par écrit avant la séance.</p>
        <p>Les photographies brutes (RAW) ne sont pas fournies. Seuls les fichiers retouchés finaux sont livrés au Client.</p>
      </Section>

      <Section title="8. Livraison">
        <p>Les délais de livraison indicatifs sont :</p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Séance photo standard : photos remises sur place le jour de la séance</li>
          <li>Reportage mariage : sous 4 à 6 semaines</li>
          <li>Reportage baptême/événement : sous 1 à 2 semaines</li>
        </ul>
        <p>La livraison se fait par lien de téléchargement sécurisé, clé USB (en supplément) ou via une galerie en ligne privée.</p>
      </Section>

      <Section title="9. Cartes cadeaux">
        <p>Les cartes cadeaux sont nominatives et ne sont activées qu'après réception complète du paiement. Le code est ensuite transmis par email au bénéficiaire.</p>
        <p><strong className="text-white">Durée de validité :</strong> illimitée. Les cartes cadeaux ne peuvent pas être remboursées en espèces.</p>
      </Section>

      <Section title="10. Réclamations et litiges">
        <p>Toute réclamation doit être adressée par email à <a href="mailto:contact@ludovic-garcia-photographe.fr" className="text-gold hover:underline">contact@ludovic-garcia-photographe.fr</a> dans les 14 jours suivant la livraison.</p>
        <p>En cas de litige non résolu à l'amiable, le Client peut recourir gratuitement à un médiateur de la consommation. À défaut, les tribunaux français sont seuls compétents.</p>
      </Section>

      <Section title="11. Acceptation des CGV">
        <p>La validation de la réservation ou de l'achat d'une carte cadeau vaut acceptation pleine et entière des présentes CGV.</p>
      </Section>
    </>
  );
}

function Privacy() {
  return (
    <>
      <Section title="Responsable du traitement">
        <p>Les données personnelles collectées sur ce site sont traitées par <strong className="text-white">Ludovic Garcia — Photographe</strong>, 270 rue Capitaine, 13270 Fos-sur-Mer.</p>
        <p>Contact : <a href="mailto:contact@ludovic-garcia-photographe.fr" className="text-gold hover:underline">contact@ludovic-garcia-photographe.fr</a></p>
      </Section>

      <Section title="Données collectées">
        <p>Nous collectons uniquement les données strictement nécessaires :</p>
        <ul className="list-disc ml-6 space-y-1">
          <li><strong className="text-white">Formulaire de réservation :</strong> nom, email, téléphone, ville, adresse (pour calculer les frais de trajet)</li>
          <li><strong className="text-white">Formulaire de contact :</strong> nom, email, téléphone, message</li>
          <li><strong className="text-white">Carte cadeau :</strong> nom et email de l'acheteur et du bénéficiaire</li>
        </ul>
      </Section>

      <Section title="Finalités">
        <p>Ces données sont utilisées pour :</p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Gérer les réservations et livrer les prestations</li>
          <li>Envoyer les confirmations, instructions de paiement et rappels</li>
          <li>Répondre à vos demandes de contact</li>
          <li>Établir les factures et respecter nos obligations comptables</li>
        </ul>
        <p>Aucune donnée n'est utilisée à des fins de publicité ou de prospection commerciale.</p>
      </Section>

      <Section title="Durée de conservation">
        <ul className="list-disc ml-6 space-y-1">
          <li>Données de réservation confirmée : 10 ans (obligation comptable)</li>
          <li>Réservations non confirmées : supprimées sous 6 mois</li>
          <li>Messages de contact : supprimés sous 3 ans maximum</li>
        </ul>
      </Section>

      <Section title="Destinataires">
        <p>Vos données sont exclusivement traitées par Ludovic Garcia. Elles peuvent être transmises à nos prestataires techniques (hébergeur, service d'emails Resend, service Google Maps pour le calcul des trajets) dans le strict cadre de leurs missions et sous accord de confidentialité.</p>
        <p><strong className="text-white">Aucune donnée n'est vendue à des tiers.</strong></p>
      </Section>

      <Section title="Vos droits (RGPD)">
        <p>Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi "Informatique et Libertés", vous disposez d'un droit :</p>
        <ul className="list-disc ml-6 space-y-1">
          <li><strong className="text-white">d'accès</strong> à vos données</li>
          <li><strong className="text-white">de rectification</strong> si elles sont inexactes</li>
          <li><strong className="text-white">d'effacement</strong> ("droit à l'oubli")</li>
          <li><strong className="text-white">de limitation</strong> du traitement</li>
          <li><strong className="text-white">de portabilité</strong> de vos données</li>
          <li><strong className="text-white">d'opposition</strong> à leur traitement</li>
        </ul>
        <p>Pour exercer ces droits, contactez <a href="mailto:contact@ludovic-garcia-photographe.fr" className="text-gold hover:underline">contact@ludovic-garcia-photographe.fr</a>. Réponse sous 30 jours maximum.</p>
        <p>En cas de désaccord, vous pouvez saisir la <strong>CNIL</strong> (<a href="https://www.cnil.fr" className="text-gold hover:underline">www.cnil.fr</a>).</p>
      </Section>

      <Section title="Cookies">
        <p>Ce site utilise uniquement des <strong className="text-white">cookies techniques strictement nécessaires</strong> à son fonctionnement (stockage local pour votre panier / réservation en cours). Aucun cookie publicitaire ou de tracking n'est utilisé.</p>
      </Section>

      <Section title="Sécurité">
        <p>Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos données : chiffrement HTTPS, hébergement dans l'UE, limitation des accès, journalisation des connexions admin, protection anti-brute-force.</p>
      </Section>
    </>
  );
}
